import styled from '@emotion/styled'
import { ModalClose } from 'app/components/Common/Header/ModalClose'
import { Image, Props as ImageProps } from 'app/components/Common/Image'
import { Line } from 'app/components/Common/Line'
import { IconLogo } from 'app/components/Icons'
import { FlexBox } from 'app/components/Layout/FlexBox'
import { useVocabularyData } from 'app/utils/vocabulary'
import disableScroll from 'disable-scroll'
import { uniqBy } from 'lodash'
import React, { memo, useEffect, useState } from 'react'
import { LazyLoadComponent } from 'react-lazy-load-image-component'

import { Filter } from './Filter'
import { Gallery } from './Gallery'

export interface CategoryProps {
  thumbnails?: ImageProps[]
  title: string
}

export interface Props {
  categories: CategoryProps[]
  label?: string
  languageCode: string
  siteName?: string
}

export const GalleryCategories = memo(function GalleryCategories({
  categories,
  label,
  languageCode,
  siteName,
}: Props) {
  if (!categories) {
    return null
  }

  const [filteredCategory, setFilteredCategory] = useState('all')
  const [modalGalleryStatus, setModalGalleryStatus] = useState(false)
  const [clickedSlide, setClickedSlide] = useState<any>(null)

  let counter = 0
  let images: any = []
  const categoriesImages = categories.map((item: any) =>
    item.images.map((e: any) => images.push(e)),
  )

  useEffect(() => {
    if (modalGalleryStatus) {
      document.addEventListener('keydown', function (e) {
        switch (e.keyCode) {
          case 27:
            disableScroll.off()
            setModalGalleryStatus(false)
            setClickedSlide(0)
            break
        }
      })
    }
  }, [modalGalleryStatus])

  return (
    <Container>
      <IconLogo />

      {label ? <Label>{label}</Label> : null}
      {siteName ? (
        <Title>
          {siteName}
          <Line />
        </Title>
      ) : null}

      {categories.length > 1 ? (
        <Wrapper>
          <Filters dial={5} row wrap>
            <Filter
              active={filteredCategory === 'all'}
              category="all"
              title={useVocabularyData('all', languageCode)}
              onClick={() => {
                setFilteredCategory('all')
              }}
            />
            {uniqBy(categories, 'title').map((item, index) => {
              const category = item.title
                .replace(/[^a-zA-Z]/g, '')
                .toLocaleLowerCase()

              return category ? (
                <Filter
                  active={filteredCategory === category}
                  category={category}
                  key={index}
                  onClick={(e) => {
                    setFilteredCategory(e.target.getAttribute('data-category'))
                  }}
                  title={item.title}
                />
              ) : undefined
            })}
          </Filters>
        </Wrapper>
      ) : null}

      <Categories row wrap>
        {uniqBy(categories, 'title').map((list) => {
          const category = list.title
            .replace(/[^a-zA-Z]/g, '')
            .toLocaleLowerCase()

          if (!list.thumbnails) {
            return null
          }

          return list.thumbnails.map((item, index) => {
            counter = counter + 1

            return (
              <Thumb
                className={
                  filteredCategory === category || filteredCategory === 'all'
                    ? 'visible'
                    : undefined
                }
                data-counter={counter}
                key={index}
                onClick={(e) => {
                  if (!modalGalleryStatus) {
                    setModalGalleryStatus(true)
                    disableScroll.on()
                  } else {
                    setModalGalleryStatus(false)
                    disableScroll.off()
                  }

                  const realClickedSlide =
                    Number(e.currentTarget.getAttribute('data-counter')) - 1

                  setClickedSlide(realClickedSlide)
                  setTimeout(() => {
                    setClickedSlide(null)
                  }, 100)
                }}
              >
                <LazyLoadComponent>
                  <Image {...item} />
                </LazyLoadComponent>
              </Thumb>
            )
          })
        })}
      </Categories>

      <Modal className={modalGalleryStatus ? ' open' : undefined}>
        <ModalClose
          languageCode={languageCode}
          onClick={() => {
            disableScroll.off()
            setModalGalleryStatus(false)
            setClickedSlide(0)
          }}
          variant="dark"
        />
        <Gallery
          clickedSlide={clickedSlide}
          images={images}
          isOpen={modalGalleryStatus}
        />
      </Modal>
    </Container>
  )
})

const Container = styled.section`
  margin: 9.25rem 0 8.125rem;
  padding: 0 10.556vw;
  text-align: center;

  > svg {
    width: 4.9375rem;
    height: auto;
    fill: ${({ theme }) => theme.colors.variants.neutralDark2};
    margin-bottom: 3.25rem;
  }

  @media (max-width: 1199px) {
    margin: 8.125rem 0 7.5rem;
    padding: 0 1.875rem;

    > svg {
      width: 3.75rem;
      margin-bottom: 2.25rem;
    }
  }

  @media (max-width: 767px) {
    padding-right: 1.25rem;
    padding-left: 1.25rem;
  }
`

const Label = styled.h1`
  color: ${({ theme }) => theme.colors.variants.secondaryLight};
  font-family: ${({ theme }) => theme.fontFamily.paragraph};
  font-size: 1.0625rem;
  font-weight: 600;
  line-height: 1.25rem;
  margin-bottom: 0.9375rem;
  text-transform: uppercase;

  @media (max-width: 1199px) {
    font-size: 1rem;
  }
`

const Title = styled.h2`
  color: ${({ theme }) => theme.colors.variants.neutralDark2};
  font-family: ${({ theme }) => theme.fontFamily.heading};
  font-size: 3rem;
  font-weight: 500;
  line-height: 4rem;

  > div {
    margin: 1.875rem auto 0;
  }

  @media (max-width: 1199px) {
    font-size: 2.25rem;
    line-height: 2.875rem;

    > div {
      margin-top: 1.25rem;
    }
  }
`

const Wrapper = styled.div`
  max-width: 67.25rem;
  margin: 6.4375rem auto 0;
  position: relative;

  > svg {
    width: 100%;
    height: auto;
    fill: ${({ theme }) => theme.colors.variants.neutralLight3};
    opacity: 0.6;
    position: absolute;
    top: 60%;
    left: 0;
    transform: translateY(-50%);
    z-index: -1;
  }

  @media (max-width: 1023px) {
    max-width: none;
    margin-top: 0;
  }
`

const Filters = styled(FlexBox)`
  margin: 4rem 0;

  @media (max-width: 1199px) {
    margin: 3.125rem 0 1.875rem;
  }

  @media (max-width: 1023px) {
    margin: 2.125rem 0 0;
  }
`

const Categories = styled(FlexBox)`
  margin: 4.375rem -1.875rem 0 0;

  @media (max-width: 1199px) {
    margin: 3.125rem -1.5rem 0 0;
  }

  @media (max-width: 767px) {
    margin: 1.25rem -1.25rem 0 0;
  }
`

const Thumb = styled.div`
  display: none;
  width: calc(33.333% - 1.875rem);
  cursor: pointer;
  margin: 1.875rem 1.875rem 0 0;
  overflow: hidden;
  padding-bottom: 23%;
  position: relative;
  &:hover {
    img {
      transform: scale(1.1);
    }
  }
  &.visible {
    display: block;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    position: absolute;
    top: 0;
    left: 0;
    transition: 0.3s ease-in-out;
  }

  @media (max-width: 1199px) {
    width: calc(33.333% - 1.5rem);
    margin: 1.5rem 1.5rem 0 0;
  }

  @media (max-width: 767px) {
    width: calc(50% - 1.25rem);
    margin: 1.25rem 1.25rem 0 0;
    padding-bottom: calc(50% - 1.25rem);
  }
`

const Modal = styled.div`
  width: 100%;
  height: 100%;
  opacity: 0;
  position: fixed;
  top: 0;
  left: 0;
  visibility: hidden;
  z-index: -1;
  transition: 0.15s ease-out;
  &.open {
    opacity: 1;
    visibility: visible;
    z-index: 9999;
  }

  @media (max-width: 1199px) {
    > div {
      &:first-of-type {
        top: 1.625rem;
        right: 1.875rem;
      }
    }
  }
`
